import React from 'react';
import { IoMdCheckmark } from 'react-icons/io';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import {
  success as toastSuccess,
  error as toastError
} from 'legacy/common/utils/toast';

import Link from 'next/link';
import { requestJoinGroup } from 'legacy/search/hooks';
import AuthenticationFlow from 'legacy/auth/components/authentication-flow/AuthenticationFlow';
import HourglassIcon from './components/HourglassIcon';

interface OrgCardProps {
  id: string;
  name: string;
  address: string;
  badgeImageUrl: string;
  membersCount: number;
  userMembership?: {
    status: 'pending' | 'approved' | 'rejected' | null;
    role: 'member' | 'admin' | 'owner' | null;
  };
  associationId: string;
  createdDate?: string;
  user?: any;
}

const OrgCard: React.FC<OrgCardProps> = ({
  id,
  name,
  address,
  badgeImageUrl,
  membersCount,
  userMembership,
  associationId,
  createdDate = 'Recently',
  user
}) => {
  const { t } = useTranslation(['organizations']);
  const queryClient = useQueryClient();
  const { mutate: requestMembership, isLoading } = requestJoinGroup();

  const handleJoinClick = () => {
    if (
      userMembership?.status === 'pending' ||
      userMembership?.status === 'approved' ||
      isLoading
    )
      return;

    requestMembership(
      { groupId: id },
      {
        onSuccess: () => {
          toastSuccess(t('organizations:publicView.joinRequestSent'));

          queryClient.invalidateQueries([
            'associationGroups',
            'list',
            { associationId }
          ]);
        },
        onError: (error) => {
          toastError(t('organizations:publicView.joinRequestFailed'));
        }
      }
    );
  };

  const handleAuthenticatedJoin = () => {
    handleJoinClick();
  };

  const getButtonState = () => {
    if (userMembership !== undefined) {
      if (userMembership?.status === 'pending') {
        return {
          text: null,
          icon: <HourglassIcon />,
          disabled: true,
          className: 'bg-[#F6F6F6] w-[52px]'
        };
      }

      if (
        userMembership?.status === 'approved' ||
        userMembership?.role === 'admin' ||
        userMembership?.role === 'owner'
      ) {
        return {
          text: null,
          icon: <IoMdCheckmark className='text-[#3853C4]' size={20} />,
          disabled: true,
          className: 'bg-white border-1 border-[#EFF2FF] w-[52px]'
        };
      }
    }

    return {
      text: '+',
      icon: null,
      disabled: isLoading,
      className:
        'bg-[#EFF2FF] text-[#3853C4] hover:bg-[#3853C4] hover:text-white text-xl w-[52px]'
    };
  };

  const buttonState = getButtonState();

  return (
    <div className="rounded-2xl p-2 transition-all duration-300 ease-in-out hover:-translate-y-1 hover:shadow-xl hover:scale-[1.01] active:scale-95 relative">
      <Link
        href={`/organizations/${id}`}
        className="block w-full h-full rounded-2xl cursor-pointer"
      >
        {/* Organization Badge and Info */}
        <div
          className="flex items-start gap-3 mb-4 rounded-2xl w-full p-[1px]"
          style={{
            background:
              'linear-gradient(to right, #EBEBEB 30%,#E6E6E6 30%,#fff 100%)'
          }}
        >
        <div
          className="w-[138px] h-[138px] bg-gray-100 rounded-2xl border-2 overflow-hidden border-white"
          style={{ boxShadow: '0px 8.02px 23.2px rgba(66, 84, 152, 0.1)' }}
        >
          <img
            src={badgeImageUrl}
            alt={`${name}'s badge`}
            className="w-full h-full object-cover"
          />
        </div>

        <div className="self-center flex flex-col text-gray-600 text-sm tracking-[-0.02em] mb-2">
          <span className="font-normal">{address}</span>
          <span className="font-semibold">{t('organizations:directory.createdLabel')} {createdDate}</span>
        </div>
      </div>

      {/* Organization Status and Actions */}
      <div className="flex items-start">
        <div className="flex flex-col justify-center gap-1">
          <p className="font-black text-xl tracking-[0.02px]">{name}</p>
          <div className="flex flex-col gap-2 text-sm font-normal ">
            <p className="">{t('organizations:directory.membersCount', { count: membersCount })}</p>
          </div>
        </div>
      </div>
      </Link>
      
      {/* Join button positioned absolutely outside the Link */}
      <div className="absolute bottom-2 right-2">
        {buttonState && (
          <>
            {user ? (
              <button
                onClick={handleJoinClick}
                disabled={buttonState.disabled}
                className={`ml-1 px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                  buttonState.className
                } ${
                  buttonState.disabled ? 'cursor-not-allowed' : 'cursor-pointer'
                }`}
              >
                <span className="flex items-center justify-center gap-2">
                  {buttonState.icon}
                  {buttonState.text}
                </span>
              </button>
            ) : (
              <AuthenticationFlow onCompleteAuthentication={handleAuthenticatedJoin}>
                {({ startAuthenticationFlow }) => (
                  <button
                    onClick={startAuthenticationFlow}
                    disabled={buttonState.disabled}
                    className={`ml-1 px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                      buttonState.className
                    } ${
                      buttonState.disabled ? 'cursor-not-allowed' : 'cursor-pointer'
                    }`}
                  >
                    <span className="flex items-center justify-center gap-2">
                      {buttonState.icon}
                      {buttonState.text}
                    </span>
                  </button>
                )}
              </AuthenticationFlow>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default OrgCard;
